"use client";

import { useCallback, useEffect, useState } from "react";
import { getAdminCourseMetadataApiV1AdminCoursesCourseIdMetadataGet } from "@/src/api/sdk.gen";
import { apiFetch } from "@/src/lib/api-fetch";
import type { CourseMetadataItem } from "@/lib/admin/course-details";

export function useCourseMetadata({ id }: { id: string }) {
  const [domains, setDomains] = useState<CourseMetadataItem[]>([]);
  const [goals, setGoals] = useState<CourseMetadataItem[]>([]);
  const [departments, setDepartments] = useState<CourseMetadataItem[]>([]);
  const [newDomainLabel, setNewDomainLabel] = useState("");
  const [newGoalLabel, setNewGoalLabel] = useState("");
  const [newDepartmentLabel, setNewDepartmentLabel] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [metadataBusyKey, setMetadataBusyKey] = useState<string | null>(null);

  const fetchMetadata = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/admin/courses/${id}/metadata`);
      if (res.ok) {
        const data = await res.json();
        setDomains((data.domains || []) as CourseMetadataItem[]);
        setGoals((data.goals || []) as CourseMetadataItem[]);
        setDepartments((data.departments || []) as CourseMetadataItem[]);
      }
    } catch (error) {
      console.error("Failed to fetch metadata:", error);
    }
  }, [id]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  const handleAddMetadata = async (type: "DOMAIN" | "FUTURE_GOAL" | "DEPARTMENT") => {
    const rawLabel = type === "DOMAIN" ? newDomainLabel : type === "FUTURE_GOAL" ? newGoalLabel : newDepartmentLabel;
    const label = rawLabel.trim();
    if (!label) return;
    const busyKey = `add:${type}`;
    setMetadataBusyKey(busyKey);
    try {
      const res = await apiFetch(`/api/admin/courses/${id}/metadata`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, label }),
      });
      if (!res.ok) throw new Error("Failed to add metadata item");
      if (type === "DOMAIN") setNewDomainLabel("");
      else if (type === "FUTURE_GOAL") setNewGoalLabel("");
      else setNewDepartmentLabel("");
      await fetchMetadata();
    } catch (error: any) {
      alert(error?.message || "Failed to add item");
    } finally {
      setMetadataBusyKey(null);
    }
  };

  const handleSaveMetadataLabel = async (metadataId: number) => {
    const label = editingLabel.trim();
    if (!label) return;
    const busyKey = `save:${metadataId}`;
    setMetadataBusyKey(busyKey);
    try {
      const res = await apiFetch(`/api/admin/courses/${id}/metadata/${metadataId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label }),
      });
      if (!res.ok) throw new Error("Failed to update item");
      setEditingId(null);
      setEditingLabel("");
      await fetchMetadata();
    } catch (error: any) {
      alert(error?.message || "Failed to update item");
    } finally {
      setMetadataBusyKey(null);
    }
  };

  const handleDeleteMetadata = async (metadataId: number) => {
    if (!confirm("Delete this item?")) return;
    const busyKey = `delete:${metadataId}`;
    setMetadataBusyKey(busyKey);
    try {
      const res = await apiFetch(`/api/admin/courses/${id}/metadata/${metadataId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete item");
      if (editingId === metadataId) {
        setEditingId(null);
        setEditingLabel("");
      }
      await fetchMetadata();
    } catch (error) {
      alert(error instanceof Error ? error.message : "Failed to delete item");
    } finally {
      setMetadataBusyKey(null);
    }
  };

  return {
    domains,
    goals,
    departments,
    newDomainLabel,
    setNewDomainLabel,
    newGoalLabel,
    setNewGoalLabel,
    newDepartmentLabel,
    setNewDepartmentLabel,
    editingId,
    setEditingId,
    editingLabel,
    setEditingLabel,
    metadataBusyKey,
    fetchMetadata,
    handleAddMetadata,
    handleSaveMetadataLabel,
    handleDeleteMetadata,
  };
}
